/**
 * TELV1-186 — Single source of truth for project-type-aware visual
 * identity and copy across the Tellet dashboard.
 *
 * The Tellet Embed product is a tier-distinct project type. After the
 * project creation modal (where Interview/Embed is chosen with
 * distinct iconography), the rest of the dashboard previously fell
 * back to generic "interview" UI — collapsing the product distinction.
 * This helper carries the embed identity through every surface so an
 * embed project ALWAYS looks and reads as Tellet Embed, not "interview
 * with a few options turned off".
 *
 * Used by:
 *   - Project list (workspace table + grid) — pill next to title
 *   - Project detail header / breadcrumb — pill in the title bar
 *   - Design tab — context banner + question-count usage hint
 *   - Tabs — "Share" → "Embed" for embed projects
 *   - Status indicator — "Live as embed" rather than "Published"
 *   - Status menu — "Make embed live" CTA
 *
 * Adding a new project type later is a one-row change here, not
 * eight components.
 */
import type { LucideIcon } from 'lucide-react';
import { Code, MessageSquare } from 'lucide-react';
import type { ProjectType } from '@/types';

export type ProjectTypeKind = 'interview' | 'embed';

export interface ProjectTypeStatusLabels {
  /** Replacement for the "Published" status label */
  published: string;
  /** Replacement for the "Make live" / "Publish" CTA */
  makeLive: string;
}

export interface ProjectTypeTabLabels {
  /**
   * Label for the "Share" tab. Embed projects rename this to "Embed"
   * because the primary action on that tab IS the embed code, not
   * panel sharing.
   */
  share: string;
}

export interface ProjectTypeHelperCopy {
  /** Short i18n-key tag for the top-of-design-tab context banner */
  designBannerKey: string;
  /** Inline helper for the question editor when the cap is relevant */
  questionCounterKey: string;
}

export interface ProjectTypeConfig {
  kind: ProjectTypeKind;
  /** Visual identity */
  icon: LucideIcon;
  /**
   * Tailwind class set for the project-type pill background + text.
   * Purple for embed (matches the creation card); blue for interview.
   */
  pillClassName: string;
  /** Plain-English name shown on the pill */
  pillLabel: string;
  /** i18n key for the pill label (preferred over pillLabel where translations exist) */
  pillLabelKey: string;
  /** Maximum number of interview questions allowed for this type */
  maxQuestions: number | null;
  /** Visibility flags for sections that diverge per project type */
  showScreening: boolean;
  showPanel: boolean;
  /** Status labels (Published → "Live as embed" etc.) */
  statusLabels: ProjectTypeStatusLabels;
  /** Tab labels (Share → Embed for embed) */
  tabLabels: ProjectTypeTabLabels;
  /** Helper copy keys */
  helperCopy: ProjectTypeHelperCopy;
}

const INTERVIEW_CONFIG: ProjectTypeConfig = {
  kind: 'interview',
  icon: MessageSquare,
  pillClassName: 'bg-interview-bg text-interview',
  pillLabel: 'Interview',
  pillLabelKey: 'msg.telletInterview',
  maxQuestions: null,
  showScreening: true,
  showPanel: true,
  statusLabels: {
    // Status labels for interview projects mirror the existing
    // STATUS_CONFIG in ProjectStatusMenu.tsx ('statusLive' for PUBLISHED).
    // For embed they're overridden to the dedicated TELV1-186 keys.
    published: 'msg.statusLive',
    makeLive: 'msg.publish',
  },
  tabLabels: { share: 'msg.share' },
  helperCopy: {
    designBannerKey: '', // no banner for interview — default UI
    questionCounterKey: '',
  },
};

const EMBED_CONFIG: ProjectTypeConfig = {
  kind: 'embed',
  icon: Code,
  pillClassName: 'bg-embed-bg text-embed',
  pillLabel: 'Embed',
  pillLabelKey: 'msg.telletEmbed',
  // TELV1-457 — embeds are not flat-capped; they cap per block (≤3 questions)
  // and on block count (≤10), enforced in project.validation / DiscussionGuide.
  maxQuestions: null,
  showScreening: false,
  showPanel: false,
  statusLabels: {
    // Embed projects show the same "Live" status as interviews (no
    // "Live as embed" — the embed pill already conveys the type).
    published: 'msg.statusLive',
    makeLive: 'msg.makeEmbedLive',
  },
  tabLabels: { share: 'msg.embed' },
  helperCopy: {
    designBannerKey: 'msg.embedDesignBanner',
    questionCounterKey: 'msg.embedStructureCounter',
  },
};

/**
 * Returns the visual + copy configuration for a project type.
 * Treats undefined/legacy projects as 'interview' (the default that
 * preceded embed productization).
 */
export function getProjectTypeConfig(projectType: ProjectType | undefined): ProjectTypeConfig {
  if (projectType === 'embed') return EMBED_CONFIG;
  return INTERVIEW_CONFIG;
}

/** Convenience: is this an embed project? */
export function isEmbedProjectType(projectType: ProjectType | undefined): boolean {
  return projectType === 'embed';
}
